import * as React from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = import.meta.env.VITE_API_URL;

interface City {
  citiesId: number;
  name: string;
}
interface Manager {
  managerId: number;
  name: string;
}
interface Shop {
  shopId: number;
  name: string;
  address: string;
  workTime?: string;
  city?: City | null;
  manager?: Manager | null;
}

export default function ShopsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [name, setName] = React.useState('');
  const [address, setAddress] = React.useState('');

  const [cities, setCities] = React.useState<City[]>([]);
  const [city, setCity] = React.useState<City | null>(null);

  const [managers, setManagers] = React.useState<Manager[]>([]);
  const [manager, setManager] = React.useState<Manager | null>(null);
  const [managerQuery, setManagerQuery] = React.useState('');

  const fetchShop = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/shops/${id}`);
      setName(data.name);
      setAddress(data.address);
      setCity(data.city || null);
      setManager(data.manager || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/cities`);
      setCities(data.cities || []);
    } catch (err) {
      console.error(err);
    }
  };

  React.useEffect(() => {
    fetchShop();
    fetchCities();
  }, [id]);

  React.useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/managers`, {
          params: { q: managerQuery },
        });
        setManagers(data.data || data);
      } catch (err) {
        console.error(err);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [managerQuery]);

  const handleSave = async () => {
    if (!name.trim() || !address.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/shops/${id}`, {
        name,
        address,
        cityId: city?.citiesId || null,
        managerId: manager?.managerId || null,
      });
      navigate(-1);
    } catch (err: any) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, mx: 2 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2 }}
      >
        Назад
      </Button>

      <Typography variant="h5" sx={{ mb: 3 }}>
        Редагування магазину
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Назва"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Адреса"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Autocomplete
          options={cities}
          getOptionLabel={(o) => o.name}
          value={city}
          onChange={(_, v) => setCity(v)}
          renderInput={(params) => (
            <TextField {...params} label="Місто" sx={{ mb: 2 }} />
          )}
        />

        <Autocomplete
          options={managers}
          getOptionLabel={(o) => o.name}
          value={manager}
          onChange={(_, v) => setManager(v)}
          inputValue={managerQuery}
          onInputChange={(_, v) => setManagerQuery(v)}
          renderInput={(params) => <TextField {...params} label="Менеджер" />}
        />
      </Paper>

      <Button
        variant="contained"
        fullWidth
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Збереження...' : 'Зберегти'}
      </Button>
    </Box>
  );
}
